<script lang="ts" setup>
import type { FormSubmitEvent } from '@nuxt/ui';
import { object, string, array, number } from 'yup';
import type { BaselineVariables, BaselineVariableDegreeDaysType } from '~/app-modules/baseline/types';

type Schema = {
  variables: BaselineVariables[number]['name'][];
  DJUType: BaselineVariableDegreeDaysType;
  DJUBase: number;
};

// Composables
const { t } = useI18n();
const { variables } = storeToRefs(baselineStore());

// Props & Emits
const emit = defineEmits<{
  (e: 'next' | 'previous'): void;
}>();

// Data
const form = useTemplateRef('form');
const items = ref([
  {
    value: 'degree_days',
    label: t('baseline.variables_outside_temperature'),
  },
  {
    value: 'production',
    label: t('baseline.variables_production'),
  },
  {
    value: 'humidity',
    label: t('baseline.variables_outside_humidity'),
    disabled: true,
  },
]);
const DJUTypeItems = ref<{ value: string; label: string }[]>([
  { value: 'heating', label: t('baseline.variables_DJU_heating') },
  { value: 'cooling', label: t('baseline.variables_DJU_cooling') },
]);

// Form
const schema = object({
  variables: array().of(string().required(t('global.form.required'))),
  DJUType: string().required(t('global.form.required')),
  DJUBase: number().required(t('global.form.required')),
});

const state = reactive<Schema>({
  variables: [],
  DJUType: 'heating',
  DJUBase: 0,
});

// Methods
const submit = (event: FormSubmitEvent<Schema>) => {
  emit('next');
  const newVariables: BaselineVariables = [];
  event.data.variables?.forEach((variable) => {
    if (variable === 'degree_days') {
      newVariables.push({
        name: 'degree_days',
        type: state.DJUType,
        base: state.DJUBase,
      });
    } else {
      newVariables.push({
        name: variable,
      });
    }
  });
  variables.value = newVariables;
};

onMounted(() => {
  state.variables = variables.value.map((variable) => variable.name);
  state.DJUType = variables.value.find((variable) => variable.name === 'degree_days')?.type || 'heating';
  state.DJUBase = variables.value.find((variable) => variable.name === 'degree_days')?.base || 0;
});
</script>
<template>
  <u-form ref="form" :state="state" value-key="value" :schema="schema" @submit="submit">
    <div class="w-full bg-white rounded-lg p-4 border border-gray-200">
      <!-- Title -->
      <h2 class="text-lg font-bold">
        {{ $t('baseline.variables_title') }}
      </h2>
      <p class="text-sm text-gray-500">
        {{ $t('baseline.variables_description') }}
      </p>

      <!-- Form -->
      <div class="mt-4 grid grid-cols-2 gap-4">
        <u-checkbox-group
          v-model="state.variables"
          key-field="value"
          name="variables"
          indicator="end"
          variant="card"
          :items="items"
          class="col-span-2"
          :ui="{ fieldset: 'grid grid-cols-2 gap-4' }"
        >
          <template #label="{ item }">
            <div class="flex items-center gap-2 font-semibold">
              {{ item.label }}
            </div>
            <div v-if="item.value === 'degree_days'" class="mt-2 grid grid-cols-2 gap-4">
              <u-select
                v-model="state.DJUType"
                :label="$t('baseline.variables_DJU_type')"
                :items="DJUTypeItems"
                label-key="label"
                value-key="value"
              />
              <u-input-number
                v-model="state.DJUBase"
                :label="$t('baseline.variables_DJU_base')"
                orientation="vertical"
                :format-options="{
                  style: 'unit',
                  unit: 'celsius',
                  signDisplay: 'exceptZero',
                  minimumFractionDigits: 1,
                }"
              />
            </div>
          </template>
        </u-checkbox-group>

        <p v-if="form?.errors" class="text-red-500 text-sm col-span-2">
          {{ form?.errors[0]?.message }}
        </p>
      </div>
    </div>

    <div class="w-full mt-8 flex flex-row justify-between">
      <u-button color="neutral" variant="outline" :label="$t('global.cancel')" />
      <div class="flex items-center gap-2">
        <u-button color="neutral" variant="outline" :label="$t('global.previous')" @click="$emit('previous')" />
        <u-button color="primary" variant="solid" type="submit" :label="$t('global.next')" />
      </div>
    </div>
  </u-form>
</template>
